import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, postDataAPI, putDataAPI, deleteDataAPI } from '../../utils/fetchData';
import AddToCalendarButton from '../../components/Connectors/AddToCalendarButton';

const ViewAllTourManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('home');
  const [shows, setShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  
  // Tour settings
  const [homeCity, setHomeCity] = useState('');
  const [bufferHours, setBufferHours] = useState(6);
  const [preferredAirlines, setPreferredAirlines] = useState({}); // { "Delta": 1, "United": 2, ... }
  const [flightDepartureTimeCutoff, setFlightDepartureTimeCutoff] = useState(18);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsInitialLoad, setSettingsInitialLoad] = useState(true); // Track if settings have been loaded
  
  // Tour plans for Tours tab
  const [tourPlans, setTourPlans] = useState([]);
  const [tourPlansLoading, setTourPlansLoading] = useState(false);
  const [deletingTourId, setDeletingTourId] = useState(null);
  
  // Multi-select state for tours
  const [selectedTours, setSelectedTours] = useState([]);
  
  // Tour status for shows (which shows are in which tours)
  const [showTourMap, setShowTourMap] = useState({});
  
  // Tour confirmation modal
  const [showTourConfirmModal, setShowTourConfirmModal] = useState(false);
  const [tourSequence, setTourSequence] = useState([]); // ordered array of show objects
  const [tourNameInput, setTourNameInput] = useState('');
  
  
  // Available airlines
  const availableAirlines = [
    'Delta',
    'American',
    'United',
    'Southwest',
    'JetBlue',
    'Air Canada'
  ];

  useEffect(() => {
    loadShows();
    loadTourSettings();
    loadShowTourStatus();
  }, []);

  useEffect(() => {
    // Load tour plans when tours tab is active
    if (activeTab === 'tours') {
      loadTourPlans();
    }
  }, [activeTab]);

  // Handle pre-selection from TravelTab
  useEffect(() => {
    const preSelectId = location.state?.preSelectShowId;
    if (preSelectId && shows.length > 0) {
      const showToSelect = shows.find(s => s._id === preSelectId);
      if (showToSelect && !isShowSelected(showToSelect)) {
        console.log('📍 Pre-selecting show:', showToSelect);
        setSelectedShows([showToSelect]);
      }
    }
  }, [location.state, shows]);

  const loadShows = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows', token);
      
      if (response.data.status === 200 && response.data.data) {
        const showsData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        console.log(`📊 Loaded ${showsData.length} tour shows`);
        
        // Filter out past shows - only keep shows that are today or in the future
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to midnight for date comparison
        
        const futureShows = showsData.filter(show => {
          if (!show.showDate) return false; // Exclude shows without dates
          
          const showDate = new Date(show.showDate);
          showDate.setHours(0, 0, 0, 0); // Set to midnight for date comparison
          
          return showDate >= today; // Keep today and future shows
        });
        
        console.log(`📅 Filtered to ${futureShows.length} future/present shows (removed ${showsData.length - futureShows.length} past shows)`);
        
        // Diagnostic: Check for shows with missing venue data
        const showsWithIssues = futureShows.filter(show => 
          !show.venue || !show.venue.city || show.venue.city === 'TBD' || show.venue.city.trim() === ''
        );
        if (showsWithIssues.length > 0) {
          console.warn(`⚠️ ${showsWithIssues.length} show(s) have incomplete venue data:`, 
            showsWithIssues.map(s => ({
              artist: s.showInfo?.artist || 'Unknown',
              venue: s.venue,
              bookingId: s.bookingId
            }))
          );
        }
        
        setShows(futureShows);
      }
    } catch (error) {
      console.error('Error loading shows:', error);
    } finally {
    }
  };

  const loadTourSettings = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/settings', token);
      
      if (response.data.status === 200 && response.data.data.settings) {
        const { 
          homeCity: savedCity, 
          bufferHours: savedBuffer,
          preferredAirlines: savedAirlines,
          flightDepartureTimeCutoff: savedCutoff
        } = response.data.data.settings;
        setHomeCity(savedCity || '');
        setBufferHours(savedBuffer || 6);
        setPreferredAirlines(savedAirlines || {});
        setFlightDepartureTimeCutoff(savedCutoff || 18);
      }
    } catch (error) {
      console.error('Error loading tour settings:', error);
    } finally {
      setSettingsInitialLoad(false); // Settings have been loaded (even if empty)
    }
  };

  const loadTourPlans = async () => {
    try {
      setTourPlansLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/tour-plans', token);
      
      if (response.data.status === 200) {
        setTourPlans(response.data.data.tourPlans || []);
      }
    } catch (error) {
      console.error('Error loading tour plans:', error);
    } finally {
      setTourPlansLoading(false);
    }
  };

  const handleDeleteTour = async (tourId, tourName) => {
    if (!window.confirm(`Are you sure you want to delete "${tourName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingTourId(tourId);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await deleteDataAPI(`user/tm/tour-plans/${tourId}`, token);
      
      if (response.data.status === 200) {
        // Remove the tour from the list
        setTourPlans(prev => prev.filter(tour => tour._id !== tourId));
        // Reload tour status to update show tour map
        loadShowTourStatus();
        alert('Tour deleted successfully');
      } else {
        alert(`Failed to delete tour: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting tour:', error);
      alert(`Failed to delete tour: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setDeletingTourId(null);
    }
  };

  // Tour selection helpers
  const handleTourSelect = (tourId) => {
    setSelectedTours(prev => {
      if (prev.includes(tourId)) {
        return prev.filter(id => id !== tourId);
      } else {
        return [...prev, tourId];
      }
    });
  };

  const handleBulkDeleteTours = async () => {
    if (selectedTours.length === 0) return;
    
    const tourNames = selectedTours.map(id => {
      const tour = tourPlans.find(t => t._id === id);
      return tour?.name || 'Unknown Tour';
    }).join(', ');

    if (!window.confirm(`Are you sure you want to delete ${selectedTours.length} tour(s)? (${tourNames}) This action cannot be undone.`)) {
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Delete all selected tours
      await Promise.all(
        selectedTours.map(tourId => 
          deleteDataAPI(`user/tm/tour-plans/${tourId}`, token)
        )
      );

      // Clear selection and refresh
      setSelectedTours([]);
      loadTourPlans();
      console.log(`✅ Deleted ${selectedTours.length} tour(s)`);
    } catch (error) {
      console.error('Error deleting tours:', error);
      alert('Failed to delete some tours. Please try again.');
    }
  };

  const loadShowTourStatus = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows/tour-status', token);
      
      if (response.data.status === 200 && response.data.data.showTourMap) {
        setShowTourMap(response.data.data.showTourMap);
      }
    } catch (error) {
      console.error('Error loading show tour status:', error);
    }
  };

  const saveTourSettings = async () => {
    try {
      setSettingsLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI('user/tm/settings', { 
        homeCity, 
        bufferHours,
        preferredAirlines,
        flightDepartureTimeCutoff
      }, token);
      
      if (response.data.status === 200) {
        alert('✅ Tour settings saved!');
      }
    } catch (error) {
      console.error('Error saving tour settings:', error);
      alert('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };
  
  const toggleAirlinePreference = (airline) => {
    setPreferredAirlines(prev => {
      const newPrefs = { ...prev };
      
      if (airline in newPrefs) {
        // Remove this airline and adjust rankings
        const removedRank = newPrefs[airline];
        delete newPrefs[airline];
        
        // Decrease ranks for airlines that were ranked after this one
        Object.keys(newPrefs).forEach(key => {
          if (newPrefs[key] > removedRank) {
            newPrefs[key] -= 1;
          }
        });
      } else {
        // Add airline with next available rank
        const maxRank = Object.keys(newPrefs).length > 0 
          ? Math.max(...Object.values(newPrefs)) 
          : 0;
        newPrefs[airline] = maxRank + 1;
      }
      
      return newPrefs;
    });
  };

  const toggleShowSelection = (show) => {
    // Only allow selecting upcoming shows for tours
    const isUpcoming = ['planning', 'flights-needed', 'travel-booked', 'confirmed'].includes(show.status);
    if (!isUpcoming) {
      alert('Only upcoming shows can be added to tours');
      return;
    }

    setSelectedShows(prev => {
      const isSelected = prev.find(s => s._id === show._id);
      if (isSelected) {
        return prev.filter(s => s._id !== show._id);
      } else {
        return [...prev, show];
      }
    });
  };

  const isShowSelected = (show) => {
    return selectedShows.find(s => s._id === show._id);
  };

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'completed', label: 'Completed' },
    { id: 'tours', label: 'Tours' },
    { id: 'all', label: 'All' }
  ];

  const getFilteredShows = () => {
    let filtered = shows;

    if (activeTab !== 'all') {
      filtered = filtered.filter(show => {
        if (activeTab === 'upcoming') {
          return ['planning', 'flights-needed', 'travel-booked', 'confirmed'].includes(show.status);
        }
        return show.status === activeTab;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(show => 
        show.venue?.name?.toLowerCase().includes(query) ||
        show.venue?.city?.toLowerCase().includes(query) ||
        show.showInfo?.artist?.toLowerCase().includes(query) ||
        show.event_title?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => new Date(a.showDate) - new Date(b.showDate));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const showDate = new Date(dateString);
    const today = new Date();
    const diffTime = showDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return null;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  const formatTourDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProgressColor = (current, total) => {
    const percentage = (current / total) * 100;
    if (percentage === 100) return 'text-green-500';
    if (percentage >= 50) return 'text-yellow-500';
    return 'text-[#FF9500]';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-[rgba(255,149,0,0.2)] text-[#FF9500]',
      completed: 'bg-green-500/20 text-green-500',
      draft: 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.5)]',
      cancelled: 'bg-red-500/20 text-red-500'
    };
    
    return styles[status] || styles.draft;
  };

  const handleGenerateTourPlan = () => {
    if (selectedShows.length < 2) {
      alert('Please select at least 2 shows to create a tour');
      return;
    }

    if (!homeCity) {
      alert('Please set your Home City in Settings first');
      setShowSettings(true);
      return;
    }

    // Validate show data before opening modal
    console.log('🔍 Validating selected shows:', selectedShows);
    const invalidShows = selectedShows.filter(show => 
      !show.venue || !show.venue.city || show.venue.city === 'TBD' || show.venue.city.trim() === ''
    );
    
    if (invalidShows.length > 0) {
      console.error('❌ Shows with invalid venue data:', invalidShows);
      const showNames = invalidShows.map(s => s.showInfo?.artist || s.event_title || 'Unknown').join(', ');
      alert(`Cannot create tour plan: The following show(s) are missing valid city information: ${showNames}.\n\nPlease edit these bookings to add city/state information.`);
      return;
    }

    // Initialize modal with selected shows sorted by date
    const sortedShows = [...selectedShows].sort((a, b) => new Date(a.showDate) - new Date(b.showDate));
    setTourSequence(sortedShows);
    
    // Clear tour name input (placeholder will show suggestion)
    setTourNameInput('');
    
    // Open modal
    setShowTourConfirmModal(true);
  };

  const handleReorderTourSequence = (showId, direction) => {
    const currentIndex = tourSequence.findIndex(s => s._id === showId);
    if ((direction === 'up' && currentIndex === 0) || 
        (direction === 'down' && currentIndex === tourSequence.length - 1)) {
      return;
    }
    
    const newOrder = [...tourSequence];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    setTourSequence(newOrder);
  };

  const handleRemoveFromSequence = (showId) => {
    setTourSequence(prev => prev.filter(s => s._id !== showId));
  };

  const handleConfirmTourPlan = async () => {
    if (tourSequence.length < 2) {
      alert('Please select at least 2 shows for your tour');
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      console.log('📤 Sending tour plan request:', {
        showIds: tourSequence.map(s => s._id),
        tourName: tourNameInput.trim() || undefined,
        showCount: tourSequence.length,
        homeCity,
        bufferHours
      });
      
      const response = await postDataAPI('user/tm/tour-router/generate', {
        showIds: tourSequence.map(s => s._id),
        tourName: tourNameInput.trim() || undefined,
        homeCity,
        bufferHours
      }, token);

      if (response.data.status === 200 && response.data.data.tourPlanId) {
        console.log('✅ Tour plan generated successfully');
        setShowTourConfirmModal(false);
        navigate(`/tour-manager/tour-overview/${response.data.data.tourPlanId}`);
      } else {
        console.error('❌ Unexpected response:', response.data);
        alert(`Failed to generate tour plan: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error generating tour plan:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to generate tour plan: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  const getSuggestedTourName = () => {
    if (tourSequence.length === 0) return '';
    const firstShowDate = new Date(tourSequence[0].showDate);
    const monthYear = firstShowDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${tourSequence.length}-Show Tour • ${monthYear}`;
  };

  const filteredShows = getFilteredShows();

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Tour Shows</span>
            <div className="flex items-center gap-4">
              
              {/* Menu Dropdown */}
              <div className="relative inline-block">
                <span 
                  className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                  onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                >
                  Menu
                </span>
                
                {showMenuDropdown && (
                  <>
                    {/* Overlay to close dropdown */}
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMenuDropdown(false)}
                    />
                    
                    {/* Dropdown Menu */}
                    <div className="absolute left-0 top-full mt-1 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-20 min-w-[120px]">
                      <div 
                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                        onClick={() => {
                          setShowSettings(!showSettings);
                          setShowMenuDropdown(false);
                        }}
                      >
                        Settings
                      </div>
                      <div 
                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                        onClick={() => {
                          setShowSearch(!showSearch);
                          setShowMenuDropdown(false);
                        }}
                      >
                        Search Shows
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  const canGoBack = window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-white text-[12px] font-normal">Tour Settings</span>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                    title="Close settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                      Home City <span className="text-[#FF9500]">*</span>
                    </label>
                    <input
                      type="text"
                      value={homeCity}
                      onChange={(e) => setHomeCity(e.target.value)}
                      placeholder="e.g., Los Angeles, New York"
                      className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                    />
                  </div>

                  <div>
                    <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                      Arrival Buffer (hours before show)
                    </label>
                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                      Minimum hours you prefer to land before showtime
                    </p>
                    <input
                      type="number"
                      value={bufferHours}
                      onChange={(e) => setBufferHours(parseInt(e.target.value) || 6)}
                      min="1"
                      max="48"
                      className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                    />
                  </div>

                  <div>
                    <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                      Flight Departure Time Cutoff
                    </label>
                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                      If show is after this time, suggest flying in the day before
                    </p>
                    <select
                      value={flightDepartureTimeCutoff}
                      onChange={(e) => setFlightDepartureTimeCutoff(parseInt(e.target.value))}
                      className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                    >
                      <option value={12} className="bg-[#1a1a1a]">12:00 PM</option>
                      <option value={14} className="bg-[#1a1a1a]">2:00 PM</option>
                      <option value={16} className="bg-[#1a1a1a]">4:00 PM</option>
                      <option value={18} className="bg-[#1a1a1a]">6:00 PM</option>
                      <option value={20} className="bg-[#1a1a1a]">8:00 PM</option>
                      <option value={22} className="bg-[#1a1a1a]">10:00 PM</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                      Preferred Airlines
                    </label>
                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                      Flights from these airlines will be ranked higher
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {availableAirlines.map(airline => {
                        const rank = preferredAirlines[airline];
                        const isSelected = airline in preferredAirlines;
                        
                        return (
                <button
                            key={airline}
                            onClick={() => toggleAirlinePreference(airline)}
                            className={`text-[11px] py-2 px-3 rounded-lg transition-all flex items-center justify-between ${
                              isSelected
                                ? 'bg-[#FF9500] text-black font-medium'
                                : 'bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] hover:border-[#FF9500]'
                            }`}
                          >
                            <span>{airline}</span>
                            {isSelected && (
                              <span className="ml-2 bg-black text-[#FF9500] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                {rank}
                              </span>
                  )}
                </button>
                        );
                      })}
            </div>
          </div>

                  <button
                    onClick={saveTourSettings}
                    disabled={settingsLoading}
                    className="w-full bg-[#FF9500] text-black text-[12px] font-medium py-2 px-4 rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    {settingsLoading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search Interface */}
          {showSearch && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-white text-[12px] font-normal">Search Shows</span>
                  </div>
                  <button
                    onClick={() => setShowSearch(false)}
                    className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                    title="Close search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter venue, city, or artist..."
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                  />
                  
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - Always Visible */}
          <div className="w-full px-4 mb-6">
            <div className={`border rounded-lg p-4 transition-all ${
              selectedShows.length > 0 
                ? 'bg-[rgba(255,149,0,0.1)] border-[#FF9500]' 
                : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)]'
            }`}>
              {/* Settings Prompt or Selection Counter */}
              {/* Only show configure button if settings have been loaded AND homeCity is empty */}
              {!settingsInitialLoad && !homeCity ? (
                <>
                  <div className="mb-3">
                    <span className="text-[rgba(255,255,255,0.5)] text-[12px]">
                      Configure your tour settings to book travel
                    </span>
                  </div>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="w-full text-[12px] font-medium py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 bg-[#FF9500] text-black hover:opacity-80"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Open Settings</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[12px] transition-colors ${
                      selectedShows.length > 0 ? 'text-white' : 'text-[rgba(255,255,255,0.5)]'
                    }`}>
                      {selectedShows.length > 0 
                        ? `${selectedShows.length} show${selectedShows.length !== 1 ? 's' : ''} selected` 
                        : 'Select 2+ shows to create a tour'}
                    </span>
                    {selectedShows.length > 0 && (
                      <button
                        onClick={() => setSelectedShows([])}
                        className="text-[rgba(255,255,255,0.5)] text-[11px] hover:text-white"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Action Button - Create Tour Only */}
                  {selectedShows.length >= 2 && (
                    <button
                      onClick={handleGenerateTourPlan}
                      className="w-full text-[12px] font-medium py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] text-[#FF9500] hover:bg-[rgba(255,149,0,0.2)]"
                      title="Create tour for selected shows"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      <span>
                        Create Tour ({selectedShows.length})
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          {!showSettings && !showSearch && (
            <div className="w-full mb-6">
              <div className="flex">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                      activeTab === tab.id 
                        ? 'text-white' 
                        : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF9500]"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Shows List / Tours List */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {activeTab === 'home' ? (
              <div className="flex flex-col gap-4 mb-8">
                <div className="mb-4 pt-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                    Everything you need to coordinate, and run your touring and team in one organized place.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Import Shows Automatically</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    All confirmed shows from Bookings flow straight into TourMngr with no extra work.
                  </p>
                </div>

                <div
                  onClick={() => {
                    setActiveTab('upcoming');
                    // Scroll to show flight/hotel search options
                  }}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Search Flights & Hotels</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Quickly search the best flight and hotel options, add confirmed info into the system, and notify your team with the click of a button.
                  </p>
                </div>

                <div
                  onClick={() => setShowSettings(true)}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Set Travel Preferences</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Save your home airport, preferred airlines, and frequent travel details for faster searching.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Guestlist & Crew</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Manage guestlist allocations, track crew details, and keep everyone aligned on access and roles.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Connect the Entire Team</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Give managers, artists, tour managers, and crew synchronized access to the same up-to-date info.
                  </p>
                </div>
              </div>
            ) : activeTab === 'tours' ? (
              // Tours Tab Content
              <>
                {/* Tours List */}
                {tourPlans.length === 0 ? (
                  <div className="text-left py-[60px]">
                    <h3 className="text-white text-[12px] font-semibold mb-2">
                      No tours found
                    </h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      Create your first tour plan to get started.
                    </p>
                    <button 
                      onClick={() => setActiveTab('upcoming')}
                      className="bg-[#FF9500] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#FF8800] transition-colors"
                    >
                      Create Tour Plan
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Selected Tours Header */}
                    {selectedTours.length > 0 && (
                      <div className="flex items-center justify-between mb-4 p-3 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg">
                        <span className="text-[#FF9500] text-[12px] font-medium">
                          Selected ({selectedTours.length})
                        </span>
                        <button
                          onClick={handleBulkDeleteTours}
                          className="text-[#FF9500] text-[12px] font-medium hover:opacity-70 transition-opacity"
                        >
                          Delete
                        </button>
                      </div>
                    )}

                    <div className="space-y-3 pb-6">
                    {tourPlans.map(tour => {
                      const overallProgress = ((tour.progress.flightsBooked + tour.progress.hotelsBooked) / 
                        (tour.progress.flightsTotal + tour.progress.hotelsTotal)) * 100;
                      const isSelected = selectedTours.includes(tour._id);
                      
                      return (
                        <div
                          key={tour._id}
                          onClick={() => handleTourSelect(tour._id)}
                          className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 cursor-pointer transition-all relative group ${
                            isSelected 
                              ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]' 
                              : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]'
                          }`}
                        >
                          {/* Delete Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTour(tour._id, tour.name);
                            }}
                            disabled={deletingTourId === tour._id}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-500/20 text-red-400 hover:text-red-300 disabled:opacity-50"
                            title="Delete tour"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>

                          <div className="flex items-start justify-between mb-3 pr-8">
                            <div className="flex-1">
                              <h3 className="text-white text-[12px] font-semibold mb-1">
                                {tour.name}
                              </h3>
                              <div className="flex items-center gap-2 text-[10px] text-[rgba(255,255,255,0.5)]">
                                <span>Home: {tour.homeCity}</span>
                                <span>•</span>
                                <span>{tour.totalDays} days</span>
                                <span>•</span>
                                <span>{tour.shows?.length || 0} shows</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tour-manager/tour-overview/${tour._id}`);
                                }}
                                className="text-[#FF9500] text-[10px] px-2 py-1 hover:bg-[rgba(255,149,0,0.1)] rounded transition-colors"
                              >
                                View
                              </button>
                              <span className={`text-[10px] px-2 py-1 rounded whitespace-nowrap ${getStatusBadge(tour.status)}`}>
                                {tour.status.toUpperCase()}
                              </span>
                            </div>
                          </div>

                            {/* Progress Bar */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[rgba(255,255,255,0.7)] text-[10px]">Progress</span>
                                <span className={`text-[10px] font-bold ${getProgressColor(
                                  tour.progress.flightsBooked + tour.progress.hotelsBooked,
                                  tour.progress.flightsTotal + tour.progress.hotelsTotal
                                )}`}>
                                  {overallProgress.toFixed(0)}%
                                </span>
                              </div>
                              <div className="w-full h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#FF9500] to-[#FF7700] transition-all duration-300"
                                  style={{ width: `${overallProgress}%` }}
                                ></div>
                              </div>
                            </div>

                            {/* Stats */}
                            <div className="flex items-center gap-4 text-[10px]">
                              <div className="flex items-center gap-1">
                                <svg className="w-3 h-3 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-[rgba(255,255,255,0.7)]">
                                  {tour.progress.flightsBooked}/{tour.progress.flightsTotal} flights
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-[rgba(255,255,255,0.7)]">
                                  {tour.progress.hotelsBooked}/{tour.progress.hotelsTotal} hotels
                                </span>
                              </div>
                            </div>

                            {/* Date */}
                            <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.1)]">
                              <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                Created {formatTourDate(tour.createdAt)}
                              </p>
                            </div>
                        </div>
                      );
                    })}
                    </div>
                  </>
                )}
              </>
            ) : (
              // Shows Tab Content
              <>
                {filteredShows.length === 0 ? (
                  <div className="text-left py-[60px]">
                    <h3 className="text-white text-[12px] font-semibold mb-2">
                      No {activeTab !== 'all' ? activeTab : ''} shows found
                    </h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      {searchQuery 
                        ? 'Try a different search'
                        : activeTab === 'completed'
                          ? 'No completed shows available right now.'
                          : 'Confirmed bookings will appear here automatically'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 pb-6">
                    {filteredShows.map((show) => {
                      const daysUntil = getDaysUntil(show.showDate);
                      const isSelected = isShowSelected(show);
                      const tourInfo = showTourMap[show._id];
                      const isUpcoming = ['planning', 'flights-needed', 'travel-booked', 'confirmed'].includes(show.status);
                      
                      return (
                        <div
                          key={show._id}
                          className={`relative border rounded-lg p-4 transition-all ${
                            isSelected
                              ? 'bg-[rgba(255,149,0,0.1)] border-[#FF9500]'
                              : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.3)]'
                          }`}
                        >
                          {/* Show Content */}
                          <div>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="text-white text-[14px] font-medium">
                                  {show.showInfo?.artist || show.event_title || 'Unknown Artist'}
                                </h3>
                                <p className="text-[rgba(255,255,255,0.7)] text-[12px]">
                                  {show.venue?.city || 'Unknown City'}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {daysUntil && (
                                  <span className="bg-[rgba(255,149,0,0.2)] text-[#FF9500] text-[10px] px-2 py-1 rounded-full">
                                    {daysUntil}
                                  </span>
                                )}
                                {tourInfo && (
                                  <span 
                                    className="bg-[rgba(147,51,234,0.2)] text-purple-400 text-[10px] px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer hover:bg-[rgba(147,51,234,0.3)] transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/tour-manager/tour-overview/${tourInfo.tourPlanId}`);
                                    }}
                                    title={`Part of ${tourInfo.tourPlanName} - Click to view`}
                                  >
                                    In Tour
                                  </span>
                                )}
                              </div>
                            </div>
                          
                            <div className="flex items-center gap-2 text-[rgba(255,255,255,0.5)] text-[11px] mb-3">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>{formatDate(show.showDate)}</span>
                            </div>

                            <div className="mb-3 text-[rgba(255,255,255,0.5)] text-[11px]" style={{ fontWeight: 300 }}>
                              {show.venue?.name || 'heartbrk'}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-3">
                              <AddToCalendarButton
                                eventData={{
                                  summary: show.showInfo?.artist || show.event_title || 'Show',
                                  date: show.showDate,
                                  location: `${show.venue?.city || ''} ${show.venue?.name || ''}`.trim() || null,
                                  description: `Show at ${show.venue?.name || 'TBD'}${show.venue?.city ? ` in ${show.venue.city}` : ''}`
                                }}
                                iconOnly={true}
                                compact={true}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleShowSelection(show);
                                }}
                                disabled={!isUpcoming}
                                className={`flex-1 text-[11px] py-2 px-3 rounded-lg transition-all ${
                                  isSelected
                                    ? 'bg-[#FF9500] text-black font-medium'
                                    : !isUpcoming
                                      ? 'bg-[rgba(255,255,255,0.02)] text-[rgba(255,255,255,0.3)] border border-[rgba(255,255,255,0.1)] cursor-not-allowed'
                                      : 'bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] hover:border-[#FF9500] hover:text-[#FF9500]'
                                }`}
                                title={!isUpcoming ? 'Only upcoming shows can be added to tours' : undefined}
                              >
                                {isSelected ? 'In Tour ✓' : 'Add to Tour'}
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tour-manager/show/${show._id}`, {
                                    state: { activeTab: 'schedule' }
                                  });
                                }}
                                className="flex-1 bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] text-[11px] py-2 px-3 rounded-lg hover:border-[#FF9500] hover:text-[#FF9500] transition-all"
                              >
                                View
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tour-manager/show/${show._id}`, {
                                    state: { activeTab: 'travel' }
                                  });
                                }}
                                className="flex-1 bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] text-[11px] py-2 px-3 rounded-lg hover:border-[#FF9500] hover:text-[#FF9500] transition-all"
                              >
                                Book Travel
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tour Confirmation Modal */}
      {showTourConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-80"
            onClick={() => setShowTourConfirmModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-[390px] max-h-[85vh] bg-[rgba(20,20,20,0.98)] border border-[rgba(255,255,255,0.2)] rounded-lg overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-4 py-4 border-b border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center justify-between">
                <h2 className="text-white text-[14px] font-semibold">Confirm Tour Details</h2>
                <button
                  onClick={() => setShowTourConfirmModal(false)}
                  className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Tour Name Input */}
              <div>
                <label className="text-[rgba(255,255,255,0.7)] text-[11px] mb-2 block">
                  Tour Name (Optional)
                </label>
                <input
                  type="text"
                  value={tourNameInput}
                  onChange={(e) => setTourNameInput(e.target.value)}
                  placeholder={getSuggestedTourName()}
                  className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                />
              </div>

              {/* Settings Display */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">Home City</span>
                  <span className="text-white text-[11px] font-medium">{homeCity}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[rgba(255,255,255,0.7)] text-[11px]">Arrival Buffer</span>
                  <span className="text-white text-[11px] font-medium">{bufferHours} hours before show</span>
                </div>
              </div>

              {/* Show Sequence */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[rgba(255,255,255,0.7)] text-[11px]">
                    Tour Sequence ({tourSequence.length} {tourSequence.length === 1 ? 'show' : 'shows'})
                  </label>
                  <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                    Reorder if needed
                  </span>
                </div>

                <div className="space-y-2">
                  {tourSequence.map((show, index) => (
                    <div
                      key={show._id}
                      className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 flex items-center gap-3"
                    >
                      {/* Order Number */}
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF9500] flex items-center justify-center">
                        <span className="text-black text-[11px] font-bold">{index + 1}</span>
                      </div>

                      {/* Show Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-[11px] font-medium truncate">
                          {show.showInfo?.artist || show.event_title || 'Unknown'}
                        </div>
                        <div className="text-[rgba(255,255,255,0.6)] text-[10px] truncate">
                          {formatDate(show.showDate)} • {show.venue?.name || 'TBD'}
                        </div>
                        <div className="text-[rgba(255,255,255,0.5)] text-[10px] truncate">
                          {show.venue?.city || 'TBD'}
                        </div>
                      </div>

                      {/* Reorder Controls */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleReorderTourSequence(show._id, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded transition-colors ${
                            index === 0
                              ? 'text-[rgba(255,255,255,0.2)] cursor-not-allowed'
                              : 'text-[rgba(255,255,255,0.5)] hover:text-white hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleReorderTourSequence(show._id, 'down')}
                          disabled={index === tourSequence.length - 1}
                          className={`p-1 rounded transition-colors ${
                            index === tourSequence.length - 1
                              ? 'text-[rgba(255,255,255,0.2)] cursor-not-allowed'
                              : 'text-[rgba(255,255,255,0.5)] hover:text-white hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveFromSequence(show._id)}
                        className="flex-shrink-0 text-[rgba(255,255,255,0.5)] hover:text-red-400 transition-colors p-1"
                        title="Remove from tour"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                {tourSequence.length === 0 && (
                  <div className="text-center py-6 text-[rgba(255,255,255,0.5)] text-[11px]">
                    No shows selected. Select at least 2 shows to create a tour.
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-4 border-t border-[rgba(255,255,255,0.1)] flex gap-3">
              <button
                onClick={() => setShowTourConfirmModal(false)}
                className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] text-white text-[12px] font-medium py-2.5 px-4 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTourPlan}
                disabled={tourSequence.length < 2}
                className="flex-1 bg-[#FF9500] text-black text-[12px] font-medium py-2.5 px-4 rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Tour Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutHeader>
  );
};

export default ViewAllTourManager;
